import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { ReconciliationSupplierDetail, UpdateCollectionPayload } from './types';

const getEndpoint = (supplierId: number) => `api/v1/cms/reconciliation/suppliers/${supplierId}/collection`;

type TData = ReconciliationSupplierDetail;
type TError = AxiosError;
type TVariables = UpdateCollectionPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateCollectionApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({
    month,
    supplierId,
    invoiceSent,
    invoiceSentAt,
    collectedAmount,
    collectionDate,
    notes,
    file,
  }: TVariables) => {
    try {
      const body = new FormData();
      body.append('invoiceSent', invoiceSent ? 'true' : 'false');
      if (invoiceSentAt !== undefined) body.append('invoiceSentAt', invoiceSentAt);
      if (collectedAmount !== undefined) body.append('collectedAmount', String(collectedAmount));
      if (collectionDate !== undefined) body.append('collectionDate', collectionDate);
      if (notes !== undefined) body.append('notes', notes);
      if (file) body.append('file', file);

      const { data } = await http.patch(getEndpoint(supplierId), body, {
        params: { month },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status) throw error;

      toast({
        description: error.response?.data?.message ?? 'Failed to save the collection panel',
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    ...options,
    mutationKey: ['reconciliation-update-collection'],
  });
}
